import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SERVER_BASE_URL } from "./Config";

const ProviderRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [npiError, setNpiError] = useState(false);
  const [salesReps, setSalesReps] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetch(`${SERVER_BASE_URL}/sales-reps`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setSalesReps(data.salesReps);
      })
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    practice_name: "",
    npi: "",
    state_licenses: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    office_phone: "",
    mobile_phone: "",
    fax: "",
    email: "",
    sales_rep: "",
    contact: "",
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async () => {
    // Client-side validation
    if (!form.first_name.trim()) { setError("First name is required"); return; }
    if (!form.last_name.trim()) { setError("Last name is required"); return; }
    if (!form.practice_name.trim()) { setError("Clinic name is required"); return; }
    if (!form.email.trim()) { setError("Email is required"); return; }
    if (!form.npi.trim()) { setError("NPI is required"); return; }
    if (!/^\d+$/.test(form.npi.trim())) { setError("NPI must be a number"); return; }
    if (!form.office_phone.trim() && !form.mobile_phone.trim()) { setError("At least one phone number is required"); return; }
    if (!form.street.trim()) { setError("Street address is required"); return; }
    if (!form.city.trim()) { setError("City is required"); return; }
    if (!form.state.trim()) { setError("State is required"); return; }
    if (!form.zip.trim()) { setError("Zip is required"); return; }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${SERVER_BASE_URL}/provider-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.message || "Failed to submit request");
      }
    } catch (err) {
      setError("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Request Submitted
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your request has been submitted. You will be contacted within the next business day regarding your request.
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/login")}>
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Request a Provider Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Fill out the form below to request a provider account. Fields marked with * are required.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Provider First Name *" fullWidth value={form.first_name} onChange={handleChange("first_name")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Provider Last Name *" fullWidth value={form.last_name} onChange={handleChange("last_name")} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Clinic Name *" fullWidth value={form.practice_name} onChange={handleChange("practice_name")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="NPI *"
              fullWidth
              value={form.npi}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d+$/.test(val)) {
                  setForm({ ...form, npi: val });
                  setNpiError(false);
                } else {
                  setNpiError(true);
                }
              }}
              error={npiError}
              helperText={npiError ? "Numbers only" : undefined}
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="State License Number(s)" fullWidth value={form.state_licenses} onChange={handleChange("state_licenses")} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Street Address *" fullWidth value={form.street} onChange={handleChange("street")} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField label="City *" fullWidth value={form.city} onChange={handleChange("city")} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="State *" fullWidth value={form.state} onChange={handleChange("state")} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Zip *" fullWidth value={form.zip} onChange={handleChange("zip")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Phone (Office) *" fullWidth value={form.office_phone} onChange={handleChange("office_phone")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Phone (Mobile)" fullWidth value={form.mobile_phone} onChange={handleChange("mobile_phone")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Fax" fullWidth value={form.fax} onChange={handleChange("fax")} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Email *" type="email" fullWidth value={form.email} onChange={handleChange("email")} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Sales Rep</InputLabel>
              <Select
                value={form.sales_rep}
                label="Sales Rep"
                onChange={(e) => setForm({ ...form, sales_rep: e.target.value as string })}
                sx={{ textAlign: "left" }}
              >
                {salesReps.map((rep) => (
                  <MenuItem key={rep.id} value={rep.name}>
                    {rep.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Additional Point of Contact Information"
              fullWidth
              multiline
              minRows={3}
              value={form.contact}
              onChange={handleChange("contact")}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
          <Button variant="outlined" fullWidth onClick={() => navigate("/login")}>
            Back to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProviderRequestForm;
